import React, { useState } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input, Select, Button, Modal, message, Tooltip } from 'antd';
import { SearchOutlined, SyncOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigation, useInvalidate } from '@refinedev/core';
import { axiosInstance, API_BASE_URL } from '../../../providers/dataProvider';


const formatPrice = (v: string | null | undefined) => {
  if (!v) return '—';
  const num = parseFloat(v);
  if (num === 0) return 'Free';
  const perM = num * 1_000_000;
  if (perM < 0.01) return `<$0.01`;
  return `$${perM.toFixed(2).replace(/\.?0+$/, '')}`;
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const sourceColors: Record<string, string> = {
  OPENROUTER: '#1890ff',
  MANUAL: '#52c41a',
};

export const LlmModelList: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string | undefined>(undefined);
  const [modalityFilter, setModalityFilter] = useState<string | undefined>(undefined);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const { push } = useNavigation();
  const invalidate = useInvalidate();

  const { tableProps } = useTable({
    resource: 'llm-models',
    filters: {
      permanent: [
        ...(searchText ? [{ field: 'search', operator: 'contains' as const, value: searchText }] : []),
        ...(sourceFilter ? [{ field: 'source', operator: 'eq' as const, value: sourceFilter }] : []),
        ...(modalityFilter ? [{ field: 'modalityType', operator: 'eq' as const, value: modalityFilter }] : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  const handleSync = async () => {
    setSyncing(true);
    try {
      const resp = await axiosInstance.post(`${API_BASE_URL}/admin/llm/models/sync-openrouter`);
      setSyncResult(resp.data);
      setSyncModalOpen(true);
      invalidate({ resource: 'llm-models', invalidates: ['list'] });
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleClone = async (id: string) => {
    try {
      const resp = await axiosInstance.post(`${API_BASE_URL}/admin/llm/models/${id}/clone`, {});
      const newId = resp.data?.id;
      if (newId) push(`/llm-models/edit/${newId}`);
      else invalidate({ resource: 'llm-models', invalidates: ['list'] });
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Clone failed');
    }
  };

  return (
    <List resource="llm-models"
      headerButtons={() => (
        <Button type="primary" icon={<SyncOutlined />} loading={syncing} onClick={handleSync}>
          Sync OpenRouter
        </Button>
      )}
    >
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search models..."
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value)}
          allowClear
          style={{ width: 320 }}
        />
        <Select
          placeholder="Filter by source"
          allowClear
          style={{ width: 160 }}
          onChange={(value) => setSourceFilter(value)}
          options={[
            { label: 'OpenRouter', value: 'OPENROUTER' },
            { label: 'Manual', value: 'MANUAL' },
          ]}
        />
        <Select
          placeholder="Filter by type"
          allowClear
          style={{ width: 160 }}
          onChange={(value) => setModalityFilter(value)}
          options={[
            { label: 'Text Only', value: 'text' },
            { label: 'Image', value: 'image' },
            { label: 'Audio / Voice', value: 'audio' },
            { label: 'Video', value: 'video' },
            { label: 'Embedding', value: 'embed' },
          ]}
        />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1400 }}>
        <Table.Column dataIndex="slug" title="Slug" width={250} ellipsis />
        <Table.Column dataIndex="name" title="Name" width={200} ellipsis />
        <Table.Column
          dataIndex="source"
          title="Source"
          width={120}
          render={(source: string) => (
            <Tag style={tagStyle(sourceColors[source] || '#898989')}>{source}</Tag>
          )}
        />
        <Table.Column
          dataIndex="modality"
          title="Type"
          width={180}
          ellipsis
          render={(modality: string) => {
            if (!modality) return '—';
            const types: string[] = [];
            if (modality.includes('image')) types.push('Image');
            if (modality.includes('audio')) types.push('Voice');
            if (modality.includes('video')) types.push('Video');
            if (modality.includes('embed')) types.push('Embed');
            if (types.length === 0 && modality.includes('text')) types.push('Text');
            else if (modality.includes('text')) types.unshift('Text');
            if (types.length === 0) types.push(modality.split('->')[0] || modality);
            return (
              <Space wrap size={2}>
                {types.map((t) => (
                  <Tag key={t} style={{ fontSize: 11, borderRadius: 9999, padding: '1px 6px' }}>{t}</Tag>
                ))}
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="contextLength"
          title="Context"
          width={100}
          sorter={(a: any, b: any) => (a.contextLength ?? 0) - (b.contextLength ?? 0)}
          render={(v: number) => v?.toLocaleString() || '—'}
        />
        <Table.Column
          dataIndex="maxCompletionTokens"
          title="Max Out"
          width={100}
          sorter={(a: any, b: any) => (a.maxCompletionTokens ?? 0) - (b.maxCompletionTokens ?? 0)}
          render={(v: number) => v?.toLocaleString() || '—'}
        />
        <Table.Column
          dataIndex="promptPrice"
          title="Prompt $/1M tok"
          width={120}
          sorter={(a: any, b: any) => parseFloat(a.promptPrice ?? '0') - parseFloat(b.promptPrice ?? '0')}
          render={(v: string) => formatPrice(v)}
        />
        <Table.Column
          dataIndex="completionPrice"
          title="Completion $/1M tok"
          width={130}
          sorter={(a: any, b: any) => parseFloat(a.completionPrice ?? '0') - parseFloat(b.completionPrice ?? '0')}
          render={(v: string) => formatPrice(v)}
        />
        <Table.Column
          dataIndex="inputCacheReadPrice"
          title="Cache Read $/1M tok"
          width={130}
          sorter={(a: any, b: any) => parseFloat(a.inputCacheReadPrice ?? '0') - parseFloat(b.inputCacheReadPrice ?? '0')}
          render={(v: string) => formatPrice(v)}
        />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          width={80}
          render={(v: boolean) => <Tag style={tagStyle(v ? '#3ecf8e' : '#898989')}>{v ? 'Yes' : 'No'}</Tag>}
        />
        <Table.Column
          title="Actions"
          width={160}
          render={(_: any, record: any) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="llm-models" />
              {record.source === 'MANUAL' && (
                <>
                  <EditButton hideText size="small" recordItemId={record.id} resource="llm-models" />
                  <DeleteButton hideText size="small" recordItemId={record.id} resource="llm-models" />
                </>
              )}
              <Tooltip title="Clone as manual">
                <Button size="small" icon={<CopyOutlined />} onClick={() => handleClone(record.id)} />
              </Tooltip>
            </Space>
          )}
        />
      </Table>

      <Modal
        title="OpenRouter Sync Result"
        open={syncModalOpen}
        onCancel={() => setSyncModalOpen(false)}
        onOk={() => setSyncModalOpen(false)}
      >
        {syncResult && (
          <div>
            <p>Synced: <strong>{syncResult.synced}</strong> models</p>
            <p>Replaced: <strong>{syncResult.deleted}</strong> old records</p>
            {syncResult.errors > 0 && <p>Errors: <strong>{syncResult.errors}</strong></p>}
            <p>Duration: <strong>{syncResult.durationMs}</strong>ms</p>
          </div>
        )}
      </Modal>
    </List>
  );
};
